/**
 * Gabaritos de enquadramento das fotografias de corpo de prova.
 *
 * «Uma possibilidade é usar um gabarito com transparência, onde no
 *  adensamento o corpo de prova é sempre redondo e a foto é tirada de
 *  cima. Isso se aplica para fotos de antes e depois. Mantenha o padrão
 *  de 1:1 para as fotos (…). Para outros ensaios, como triaxial,
 *  cisalhamento direto, terão outros gabaritos com as formas
 *  correspondentes de cada CP.»
 *  — Humberto, 02/09/2026.
 *
 * ─── Por que um gabarito, e não só um recorte quadrado ───────────────
 *
 * As fotos de CP vão para a última folha do laudo, lado a lado: a de
 * antes e a de depois. Quando cada uma foi tirada de uma distância e
 * enquadrada de um jeito, o leitor compara duas fotografias em vez de
 * comparar dois corpos de prova — e a única coisa que o par existe para
 * mostrar é o que MUDOU entre elas. O gabarito é o que faz a moldagem e
 * o desmonte caírem no mesmo tamanho na folha.
 *
 * O gabarito NÃO recorta pela forma. Ele é uma marca translúcida sobre a
 * imagem; o recorte é sempre o quadrado. Recortar em círculo jogaria
 * fora a borda do anel, o rótulo ao lado, a trinca que saiu para fora —
 * e é justamente isso que uma foto de desmonte precisa mostrar.
 *
 * ─── As formas ───────────────────────────────────────────────────────
 *
 * Cada forma é a silhueta do CP daquele ensaio na vista em que ele é
 * fotografado. Nada aqui é dimensão de norma: são proporções de
 * desenho, em fração do lado do quadro, escolhidas para sobrar margem
 * suficiente para a régua, a etiqueta e a mão.
 */

export type FormaGabarito = 'circulo' | 'anel' | 'cilindro' | 'prisma' | 'livre';

export interface Gabarito {
  forma: FormaGabarito;
  /** O que a pessoa lê enquanto enquadra. */
  dica: string;
}

/**
 * O gabarito de cada ensaio.
 *
 * A chave é o código do método (`ensaio.metodo_codigo`), e não a tela:
 * o mesmo ensaio lançado por outra tela continua sendo o mesmo corpo de
 * prova. Método desconhecido cai em `livre` — quadro sem forma, que é
 * pior que o gabarito certo e melhor que o gabarito errado.
 */
export const GABARITO_DO_ENSAIO: Record<string, Gabarito> = {
  ADNS: {
    forma: 'anel',
    dica: 'De cima, com o anel inteiro dentro do círculo.',
  },
  MR: {
    forma: 'cilindro',
    dica: 'De lado, com o corpo de prova em pé dentro do contorno.',
  },
  TRIX: {
    forma: 'cilindro',
    dica: 'De lado, com o corpo de prova em pé dentro do contorno.',
  },
  /*
   * O cisalhamento direto, pelos códigos que o catálogo usa de verdade
   * (0072): o corpo de prova é QUADRADO, como o anel e a caixa (onda 260 ·
   * RN-224). Antes a chave era «CISD», um código que nenhum método tem —
   * a foto do cisalhamento caía no quadro livre.
   */
  'CD.IN': {
    forma: 'prisma',
    dica: 'De cima, com o corpo de prova quadrado dentro do quadrado.',
  },
  'CD.NAT': {
    forma: 'prisma',
    dica: 'De cima, com o corpo de prova quadrado dentro do quadrado.',
  },
  'CD.GD': {
    forma: 'prisma',
    dica: 'De cima, com o corpo de prova quadrado (150 × 150) dentro do quadrado.',
  },
  ISC: {
    forma: 'circulo',
    dica: 'De cima, com o molde inteiro dentro do círculo.',
  },
  COMP: {
    forma: 'circulo',
    dica: 'De cima, com o molde inteiro dentro do círculo.',
  },
};

export const GABARITO_LIVRE: Gabarito = {
  forma: 'livre',
  dica: 'Enquadre o que a foto precisa mostrar.',
};

export function gabaritoDoMetodo(codigo?: string | null): Gabarito {
  if (!codigo) return GABARITO_LIVRE;
  return GABARITO_DO_ENSAIO[codigo.toUpperCase()] ?? GABARITO_LIVRE;
}

/**
 * O desenho de cada forma, em coordenadas de 0 a 100 (o lado do quadro).
 *
 * Devolve os caminhos SVG: `vazado` é o furo da máscara — o que fica
 * claro — e `traco` é o contorno visível. Nas formas com mais de um
 * traço (o anel tem dois círculos, o cilindro tem a tampa), o segundo
 * vem em `apoio`, mais fraco: ele orienta e não é obrigação.
 */
export function desenhoDoGabarito(forma: FormaGabarito): {
  vazado: string; traco: string; apoio?: string;
} | null {
  switch (forma) {
    case 'circulo':
      return {
        vazado: 'M50,10 A40,40 0 1,1 49.99,10 Z',
        traco: 'M50,10 A40,40 0 1,1 49.99,10 Z',
      };
    case 'anel':
      /* Dois círculos: a parede do anel por fora, o solo por dentro. */
      return {
        vazado: 'M50,9 A41,41 0 1,1 49.99,9 Z',
        traco: 'M50,9 A41,41 0 1,1 49.99,9 Z',
        apoio: 'M50,15 A35,35 0 1,1 49.99,15 Z',
      };
    case 'cilindro':
      /* Em pé, altura duas vezes o diâmetro (50 × 100 e 100 × 200). */
      return {
        vazado: 'M28,7 H72 V93 H28 Z',
        traco: 'M28,7 H72 V93 H28 Z',
        apoio: 'M28,7 A22,5 0 0,0 72,7',
      };
    case 'prisma':
      /* A caixa de cisalhamento direto, vista de cima: QUADRADA (RN-224). */
      return {
        vazado: 'M14,14 H86 V86 H14 Z',
        traco: 'M14,14 H86 V86 H14 Z',
        apoio: 'M14,50 H86',
      };
    default:
      return null;
  }
}
